import { Injectable } from '@angular/core';
import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { DisabledTimeConfig, DisabledTimeFn } from 'ng-zorro-antd/date-picker';

@Injectable()
export class ShiftTransferTimePickerHelperService {
  public startDateTime!: string;
  public endDateTime!: string;
  public selectedStartDateTime!: string;

  disabledStartDate = (currentDate: Date): boolean => {
    if (!this.startDateTime || !this.endDateTime) {
      return false;
    } else {
      const start = new Date(this.startDateTime);
      const end = new Date(this.endDateTime);
      return (
        isBefore(currentDate, startOfDay(start)) ||
        isAfter(currentDate, endOfDay(end))
      );
    }
  };

  disabledStartTime: DisabledTimeFn = (currentDate: Date | Date[]) => {
    if (!this.startDateTime || !this.endDateTime) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => [],
      };
    } else if (Array.isArray(currentDate)) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => [],
      };
    } else {
      return this.generateDisabledStartTimeForSingleDate(currentDate);
    }
  };

  private generateDisabledStartTimeForSingleDate(
    currentDate: Date,
  ): DisabledTimeConfig {
    const start = new Date(this.startDateTime);
    const end = new Date(this.endDateTime);

    const startHour = start.getHours();
    const startMinute = start.getMinutes();
    const endHour = end.getHours();
    const endMinute = end.getMinutes();

    return {
      nzDisabledHours: (): number[] => {
        if (isSameDay(currentDate, start) && isSameDay(currentDate, end)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour < startHour || hour > endHour,
          );
        } else if (isSameDay(currentDate, start)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour < startHour,
          );
        } else if (isSameDay(currentDate, end)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour > endHour,
          );
        } else {
          return [];
        }
      },
      nzDisabledMinutes: (selectedHour: number): number[] => {
        if (isSameDay(currentDate, start) && selectedHour === startHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (minute) => minute < startMinute,
          );
        } else if (isSameDay(currentDate, end) && selectedHour === endHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (minute) => minute >= endMinute,
          );
        } else {
          return [];
        }
      },
      nzDisabledSeconds: (): number[] => [],
    };
  }

  disabledEndDate = (currentDate: Date): boolean => {
    if (!this.selectedStartDateTime || !this.endDateTime) {
      return false;
    } else {
      const start = new Date(this.selectedStartDateTime);
      const end = new Date(this.endDateTime);
      return (
        isBefore(currentDate, startOfDay(start)) ||
        isAfter(currentDate, endOfDay(end))
      );
    }
  };

  disabledEndTime: DisabledTimeFn = (currentDate: Date | Date[]) => {
    if (!this.selectedStartDateTime || !this.endDateTime) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => [],
      };
    } else if (Array.isArray(currentDate)) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => [],
      };
    } else {
      return this.generateDisabledEndTimeForSingleDate(currentDate);
    }
  };

  private generateDisabledEndTimeForSingleDate(
    currentDate: Date,
  ): DisabledTimeConfig {
    const start = new Date(this.selectedStartDateTime);
    const end = new Date(this.endDateTime);

    const startHour = start.getHours();
    const startMinute = start.getMinutes();
    const endHour = end.getHours();
    const endMinute = end.getMinutes();

    return {
      nzDisabledHours: (): number[] => {
        if (isSameDay(currentDate, start) && isSameDay(currentDate, end)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour < startHour || hour > endHour,
          );
        } else if (isSameDay(currentDate, start)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour < startHour,
          );
        } else if (isSameDay(currentDate, end)) {
          return Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour > endHour,
          );
        } else {
          return [];
        }
      },
      nzDisabledMinutes: (selectedHour: number): number[] => {
        if (isSameDay(currentDate, start) && selectedHour === startHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (minute) => minute <= startMinute,
          );
        } else if (isSameDay(currentDate, end) && selectedHour === endHour) {
          return Array.from({ length: 60 }, (_, i) => i).filter(
            (minute) => minute > endMinute,
          );
        } else {
          return [];
        }
      },
      nzDisabledSeconds: (): number[] => [],
    };
  }
}
